import { makeAutoObservable, runInAction, toJS } from "mobx";
import ApiStore from "./ApiStore";
import SignalRService from './SignalRService';
import { UploadFile } from "antd";
import { SendBulkEmailData } from "../api/EmailApiClient";
import CountryStore from "./CountryStore";
import { JobTitle } from "../models/JobTitle";
import { Skill } from "../models/Skill";
import { Employee } from "../models/Employee";
import { CompanyWebhook } from "../models/CompanyWebhook";
import { WebhookEvent } from "../enums/WebhookEvent";
import { WebhookEventStr } from "../models/WebhookEventStr";
import { CompanyApiKey } from "../models/CompanyApiKey";
import { Company } from "../models/Company";
import { resolveTaskLink } from "../helpers/taskLink";

export const toFormEvent = (v: number): WebhookEventStr =>
  v === WebhookEvent.PreUserAdd ? "PreUserAdd" : "PostUserAdd";

export const toApiEvent = (v: WebhookEventStr): number =>
  v === "PreUserAdd" ? WebhookEvent.PreUserAdd : WebhookEvent.PostUserAdd;


interface NotificationItem {
  label: string;
  key: string;
  translationKey?: string;
  translationParams?: Record<string, any>;
  onClick: () => void;
  date: string;
  isRead: boolean;
  link: string;
}

interface TaskItem {
  key: string;
  translationKey?: string;
  translationParams?: Record<string, any>;
  label: string;
  onClick: () => void;
  isCompleted: boolean;
  date: string;
  linkTask: string;
  status: string;
  isReaded: boolean;
}

export class SiteConfig {
  constructor(
    public allowCRM: boolean,
    public allowDocumentation: boolean,
    public allowERP: boolean,
    public allowPublicOffer: boolean,
    public apiDomain: string,
    public baseCloudinary: string = "https://res.cloudinary.com/dnl3x07wo",
    public defaultLanguage: string,
    public isShowLanguage: boolean
  ) { }
}

class ApplicationStore {
  countryStore: CountryStore;
  isLoading: boolean = true;
  isLoadingUser: boolean = true;
  isAuthenticated: boolean = false;
  ApiStore!: ApiStore;
  company!: Company;
  user!: Employee;
  users!: Employee[];
  jobTitles!: JobTitle[];
  skills!: Skill[];
  notifications: NotificationItem[] = [];
  filteredActivetasks: TaskItem[] = [];
  SignalRService: SignalRService;
  isFreezeModalVisible: boolean = false;
  isUaCompany: boolean = false; //TODO: Used for discount logic, maybe in future change logic
  siteConfig: SiteConfig = {
    allowCRM: true,
    allowDocumentation: true,
    allowERP: true,
    allowPublicOffer: true,
    apiDomain: window.location.origin,
    defaultLanguage: "en",
    isShowLanguage: true,
    baseCloudinary: "https://res.cloudinary.com/dnl3x07wo",
  };

  constructor(apiStore: ApiStore, countryStore: CountryStore) {
    makeAutoObservable(this);
    this.ApiStore = apiStore;
    this.countryStore = countryStore;
    this.users = [];
    this.jobTitles = [];
    this.notifications = [];
    this.filteredActivetasks = []; // filtered tasks, all tasks are stored in TaskStore
    this.SignalRService = new SignalRService(this);
  }

  //#region Variables
  unreadCount: number = 0;
  //#endregion


  //#region Setters
  setFreezeModalVisible = (visible: boolean) => {
    this.isFreezeModalVisible = visible;
  };

  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  };

  setIsAuthenticated = (status: boolean) => {
    this.isAuthenticated = status;
  };

  setIsUaCompany = (isUaCompany: boolean) => {
    this.isUaCompany = isUaCompany;
  };

  //TODO: Used for discount logic, maybe in future change logic
  setCompany = (raw: Company) => {
    const prevTenant = this.company?.Tenant;

    this.company = {
      ...raw,
      Webhooks: (raw.Webhooks ?? []).map(this.mapWebhook),
      UsersCount: this.users.filter(u => u.isActive).length.toString(),
    };

    console.log("Company set:", toJS(this.company));

    this.checkCompanyStatus();

    if (prevTenant && prevTenant !== raw.Tenant) {
      window.dispatchEvent(
        new CustomEvent("company:changed", { detail: { from: prevTenant, to: raw.Tenant } })
      );
    }
  };

  setUser = (user: Employee) => {
    const newUser = {
      ...user,
      roles: user.roles ? [...user.roles].map(role => ({ ...role })) : []
    };
    runInAction(() => {
      this.user = newUser;
    });

    this.initNotification();
    this.initUserTasks();
    this.SignalRService.startConnection();
  };

  setLocalConfig = async (config: SiteConfig) => {
    this.siteConfig = config;
  }

  setConfigApi = (token: string) => {
    this.ApiStore.setConfigApi({
      token: localStorage.getItem("token") as string,
      companyId: "0",
      userId: 0
    });
  }
  //#endregion


  //#region Company methods
  checkCompanyStatus() {
    if (this.company?.IsFrozen) {
      this.setFreezeModalVisible(true);
    } else {
      this.setFreezeModalVisible(false);
    }
  }

  //TODO: Used for discount logic, maybe in future change logic
  checkIsUACompany = async () => {
    if (this.countryStore.availableCountries.length === 0) {
      await this.countryStore.GetAllAvailableCountries();
    }

    const cid = this.company?.CountryId || 0;
    const c = this.countryStore.getById(cid);
    const iso = c?.isoCode?.toLowerCase();
    const name = c?.name?.toLowerCase();
    this.setIsUaCompany(iso === "ua" || name === "ukraine" || name === "україна");
  };

  checkCompany = async (name: string) => {
    return await this.ApiStore.CommonApiClient.CompanyCheck(name)
  };

  uploadPhoto = async (file: UploadFile, companyId: string) => {
    this.isLoading = true;
    const photo = await this.ApiStore.CompanyApiClient.logoUpload(file, companyId).finally(() => {
      this.isLoading = false;
    });
    return photo;
  }

  editCompany = async (company: Company) => {
    this.isLoading = true;
    const apiDto = {
      ...company,
      Webhooks: (company.Webhooks ?? []).map(this.toApiWebhook), // {Id, EventType, ...}
    };

    console.log("API DTO for editing company:", apiDto);

    const res = await this.ApiStore.CompanyApiClient
      .editCompany(apiDto)
      .finally(() => (this.isLoading = false));

    if (res) {
      this.setCompany(company);
    }

    return res;
  };

  updateCompany = async () => {
    this.isLoading = true;
    if (this.company.Id) {
      await this.ApiStore.CommonApiClient.autorize().then((response) => {
        if (response.data?.company) {
          console.log("XXX", JSON.parse(response.data?.company));
          this.setCompany({
            ...JSON.parse(response.data?.company),
            UsersCount: response.data?.usersInCompany,
            ProjectCount: response.data?.projectsInCompany,
          });
        }
      }).finally(() => {
        this.isLoading = false;
      });

    }
  }
  //#endregion


  //#region Notification methods
  private mapWebhook = (raw: any): CompanyWebhook => {
    const id = raw.Id ?? raw.id;
    const eventTypeRaw = raw.EventType ?? raw.eventType;
    const httpRaw = raw.HttpMethod ?? raw.httpMethod;
    const headersRaw = raw.HeadersJson ?? raw.headersJson;
    const url = raw.Url ?? raw.url;

    return {
      id,
      eventType: eventTypeRaw === 1 || eventTypeRaw === "PreUserAdd"
        ? "PreUserAdd"
        : "PostUserAdd",
      httpMethod: httpRaw === 1 || httpRaw === "Get" ? "Get" : "Post",
      headersJson: headersRaw ?? "{}",
      url,
    };
  };

  private toApiWebhook = (w: CompanyWebhook) => ({
    Id: w.id ?? 0,
    EventType: toApiEvent(w.eventType),
    HttpMethod: w.httpMethod === "Get" ? 1 : 0,
    HeadersJson: w.headersJson ?? "",
    Url: w.url,
  });

  initNotification = async () => {
    const notifications =
      await this.ApiStore.NotificationsApiClient.getNotificationsByUserId(
        this.user.id
      );
    this.notifications = notifications.map((notification) => {
      let formattedDate;
      try {
        const dateParts = notification.date.split(' ');
        const date = dateParts[0].split('.').reverse().join('-');
        const time = dateParts[1];
        const isoDate = `${date}T${time}Z`;
        const localDate = new Date(isoDate);
        formattedDate = localDate.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }).replace(/\//g, '.').replace(',', '');

      } catch (error) {
        console.error('Error formatting date:', error);
        formattedDate = 'Invalid Date';
      }



      const translationParams = typeof notification.translationParams === 'string'
        ? JSON.parse(notification.translationParams)
        : notification.translationParams;


      return {
        label: notification.text,
        translationKey: notification.translationKey,
        translationParams: translationParams,
        key: notification.id.toString(),
        onClick: () => {
          const url = "/" + notification.link;
          window.location.href = url;
        },
        date: formattedDate,
        isRead: notification.isRead,
        link: notification.link,
      };
    });

    this.unreadCount = this.notifications.filter((n) => !n.isRead).length;
  };

  public markNotificationAsRead = async (notificationId: number): Promise<boolean> => {
    try {
      const isSuccess = await this.ApiStore.NotificationsApiClient.markNotificationAsRead(notificationId);
      return isSuccess;
    } catch (error) {
      console.error("Failed to mark notification as read", error);
      return false;
    }
  };
  //#endregion


  //#region Auth methods
  autorize = async () => {
    this.setLoading(true);
    const response = await this.ApiStore.CommonApiClient.autorize();
    const skills = await this.ApiStore.CommonApiClient.getSkills();
    const users = await this.ApiStore.EmployeesApiClient.getUsers();
    const jobTitles = await this.ApiStore.CommonApiClient.getJobTitles();
    if (users) {
      this.users = users;
    }
    if (skills) {
      this.skills = skills;
    }
    if (jobTitles) {
      this.jobTitles = jobTitles;
    }
    if (response) {
      this.setLoading(false);
      this.setIsAuthenticated(true);
      if (response.data?.company) {
        console.log("XXX", response.data?.company);
        this.setCompany({
          ...JSON.parse(response.data?.company),
          UsersCount: response.data?.usersInCompany,
          ProjectCount: response.data?.projectsInCompany,
        });
        this.checkCompanyStatus();
      }
      if (response.data?.user) {
        const userData = JSON.parse(response.data?.user);
        console.log("User data with mapped roles:", userData);
        this.setUser(userData);
      }
    } else {
      localStorage.removeItem("token");
      this.setLoading(false);
      this.setIsAuthenticated(false);
    }
  }

  autorizeGoogle = async (googleToken: string) => {
    const response = await this.ApiStore.CommonApiClient.loginGoogle(googleToken).then((response) => { return response });
    return response;
  }

  login = async (email: string, password: string): Promise<any> => {
    const result = await this.ApiStore.CommonApiClient.login(email, password);
    if (result) {
      this.ApiStore.setConfigApi({
        token: localStorage.getItem("token") as string,
        companyId: "0",
        userId: 0
      });
      await this.autorize();
      this.isAuthenticated = true;
    } else {
      this.isAuthenticated = false;
    }
  };

  logout = (): void => {
    localStorage.removeItem("token");
    this.isAuthenticated = false;
    this.isLoading = false;
    window.dispatchEvent(new CustomEvent("app:logout"));
  };

  getConfig = async (): Promise<any> => {
    const res = await this.ApiStore.CommonApiClient.getConfig();
    return res;
  }
  //#endregion


  //#region Tasks methods
  initUserTasks = async () => {
    const userTasks = await this.ApiStore.TasksApiClient.getTasksByUserId(this.user.id);

    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);

    this.filteredActivetasks = userTasks
      .filter((task) => {
        const statusesToFilter = ['Done', 'Overdue', 'Approved', 'Rejected'];
        const needsDateCheck = statusesToFilter.includes(task.status);

        if (!needsDateCheck) return true;

        try {
          if (!task.endDate) return false;

          const cleanedDate = task.endDate.split('.')[0] + 'Z';
          const taskDate = new Date(cleanedDate);

          return taskDate >= sevenDaysAgo;
        } catch (e) {
          console.warn('Invalid endDate format:', task.endDate);
          return false;
        }
      })
      .map((task) => {
        let formattedDate;
        try {
          const cleanedDate = task.startDate.split('.')[0] + 'Z';
          const localDate = new Date(cleanedDate);

          formattedDate = localDate.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }).replace(/\//g, '.').replace(',', '');
        } catch (error) {
          console.error('Error formatting task startDate:', error);
          formattedDate = 'Invalid Date';
        }

        const translationParams =
          typeof task.translationParams === 'string'
            ? JSON.parse(task.translationParams)
            : task.translationParams;

        //console.log(task)
        return {
          key: task.id.toString(),
          translationKey: task.translationKey,
          translationParams: translationParams,
          label: task.title || "",
          onClick: () => {
            window.location.href = resolveTaskLink(task.linkTask, this.company?.Tenant);
          },
          isCompleted: task.status === 'Done',
          date: formattedDate,
          linkTask: task.linkTask,
          status: task.status,
          isReaded: task.isReaded || false,
        };
      });

    console.log("Filtered active tasks:", this.filteredActivetasks);
  };

  public editUserAndHandleTasks = async (user: Employee): Promise<any> => {
    const response = await this.ApiStore.EmployeesApiClient.editUser(user);
    if (response) {
      const { companyUser, taskStatus } = response;

      this.user = companyUser;

      if (taskStatus === "Done") {
        const task = this.filteredActivetasks.find(t => t.label === "Complete your profile");
        if (task) {
          task.isCompleted = true;
          this.filteredActivetasks = [...this.filteredActivetasks];
        }
      }
      return companyUser;
    }
    return false;
  }

  public markTaskAsReaded = async (taskId: number): Promise<boolean> => {
    try {
      const isSuccess = await this.ApiStore.TasksApiClient.markTaskAsReaded(taskId);
      if (isSuccess) {
        const taskIndex = this.filteredActivetasks.findIndex(t => t.key === taskId.toString());
        if (taskIndex !== -1) {
          const updatedTask = {
            ...this.filteredActivetasks[taskIndex],
            isReaded: true
          };
          this.filteredActivetasks.splice(taskIndex, 1, updatedTask);
        }
      }
      return isSuccess;
    } catch (error) {
      console.error("Failed to mark task as readed", error);
      return false;
    }
  };

  public markTaskAsCompleted = async (taskId: number): Promise<boolean> => {
    try {
      const isSuccess = await this.ApiStore.TasksApiClient.markTaskAsCompleted(taskId);
      if (isSuccess) {
        const task = this.filteredActivetasks.find(t => t.key === taskId.toString());
        if (task) task.isCompleted = true;
      }
      return isSuccess;
    } catch (error) {
      console.error("Failed to mark task as completed", error);
      return false;
    }
  };
  //#endregion


  //#region Email methods
  public sendBulkEmail = async (data: SendBulkEmailData) => {
    try {
      const isSuccess = await this.ApiStore.EmailApiClient.sendBulkEmail(data);
      return isSuccess;
    } catch (error) {
      console.error("Failed send bulk emails", error);
      return false;
    }
  }

  checkEmail = async (email: string) => {
    return await this.ApiStore.EmployeesApiClient.EmailCheck(email)
  };

  confirmEmail = async (userId: string, code: string) => {
    return await this.ApiStore.EmployeesApiClient.ConfirmEmail(userId, code)
  };

  confirmChangedEmail = async (oldEmail: string, newEmail: string, code: string) => {
    return await this.ApiStore.EmployeesApiClient.ConfirmChangedEmail(oldEmail, newEmail, code)
  };
  //#endregion


  //#region API Key methods
  getAPI = async (): Promise<CompanyApiKey[]> => {
    const res = await this.ApiStore.CompanyApiClient.getApiKey();
    return res;
  }

  generateAPI = async (): Promise<CompanyApiKey> => {
    const res = await this.ApiStore.CompanyApiClient.generateApiKey();
    return res;
  }

  deleteAPI = async (id: string) => {
    const res = await this.ApiStore.CompanyApiClient.deleteApiKey(id);
    return res;
  }
  //#endregion


  //#region Skills methods
  addSkillToStore = async (titleNewSkill: string): Promise<Skill> => {
    const createNewSkill = await this.ApiStore.CommonApiClient.addSkill(titleNewSkill);

    if (createNewSkill && typeof createNewSkill !== 'boolean') {
      this.skills.push(createNewSkill);
      return createNewSkill as Skill;
    }

    throw new Error('Failed to add skill');
  };
  //#endregion


  //#region Users methods
  getUsers = async () => {
    this.isLoadingUser = true;
    const users = await this.ApiStore.EmployeesApiClient.getUsers();
    this.isLoadingUser = false;
    this.users = users;
    return users;
  }
  //#endregion
}
export default ApplicationStore;
